/**
 * Glassware ID - item bank.
 *
 * Each entry is a single piece of lab apparatus with a stable id used for
 * de-duping within a session, a category for the end-of-match recap
 * breakdown, and a one-line "use" string surfaced as the pedagogical
 * "Why this is correct" disclosure.
 *
 * Names are kept canonical (single noun) so distractor selection from the
 * same category produces plausible but clearly distinct choices.
 */

export type GlasswareCategory = "flask" | "measuring" | "vessel" | "heating" | "support" | "separation";

export interface GlasswareItem {
  id: string;
  name: string;
  category: GlasswareCategory;
  /** Short, single-sentence description of what it's used for. */
  use: string;
  /** Longer pedagogical note shown in the "Why this is correct" disclosure. */
  why: string;
}

export const GLASSWARE_CATEGORY_LABEL: Record<GlasswareCategory, string> = {
  flask: "Flasks",
  measuring: "Measuring",
  vessel: "Vessels",
  heating: "Heating",
  support: "Support & handling",
  separation: "Separation",
};

export const glasswareItems: GlasswareItem[] = [
  // --- Flasks ---
  {
    id: "beaker",
    name: "Beaker",
    category: "vessel",
    use: "General-purpose vessel for mixing, heating, and rough volume estimates.",
    why: "Beakers have a wide opening and a pour spout. Their graduations are approximate (±5%), so they're for rough work - never for measuring exact volumes.",
  },
  {
    id: "erlenmeyer-flask",
    name: "Erlenmeyer flask",
    category: "flask",
    use: "Conical flask for swirling reactions and titrations without splashing.",
    why: "The conical body and narrow neck let you swirl the contents vigorously (e.g. during a titration) without splashing - and a stopper or condenser fits the neck easily.",
  },
  {
    id: "volumetric-flask",
    name: "Volumetric flask",
    category: "flask",
    use: "Prepares solutions of precisely known concentration.",
    why: "Calibrated to contain one exact volume at a single graduation line. Used to make standard solutions: dissolve solute, then top up to the line at the calibration temperature.",
  },
  {
    id: "florence-flask",
    name: "Florence flask",
    category: "flask",
    use: "Round-bodied flask with a flat bottom - for boiling and even heating.",
    why: "The spherical body distributes heat evenly while the flat bottom lets it stand on a tripod or hot plate. Common in distillation set-ups.",
  },
  {
    id: "round-bottom-flask",
    name: "Round-bottom flask",
    category: "flask",
    use: "Heated under reflux or in distillation; needs a stand or clamp.",
    why: "The fully spherical bottom withstands strong heating without stress fractures, but it can't stand on its own - always supported by a cork ring, clamp, or heating mantle.",
  },

  // --- Measuring ---
  {
    id: "graduated-cylinder",
    name: "Graduated cylinder",
    category: "measuring",
    use: "Measures liquid volumes to ~1% accuracy.",
    why: "More accurate than a beaker but less accurate than a pipette or volumetric flask. Read at the bottom of the meniscus, eye level with the liquid.",
  },
  {
    id: "burette",
    name: "Burette",
    category: "measuring",
    use: "Delivers variable, precisely-measured volumes - used in titrations.",
    why: "A long graduated tube with a stopcock at the bottom. You record the start and end readings; the difference is the volume delivered (typically ±0.05 mL).",
  },
  {
    id: "volumetric-pipette",
    name: "Volumetric pipette",
    category: "measuring",
    use: "Transfers one fixed, highly accurate volume of liquid.",
    why: "Has a single calibration line and a bulb in the middle. Calibrated 'to deliver' (TD): drain by gravity, then touch off the last drop - never blow it out.",
  },
  {
    id: "graduated-pipette",
    name: "Graduated pipette",
    category: "measuring",
    use: "Transfers variable measured volumes with moderate precision.",
    why: "A straight graduated tube - more flexible than a volumetric pipette but less accurate. Useful when you need several different small volumes from one tool.",
  },
  {
    id: "thermometer",
    name: "Thermometer",
    category: "measuring",
    use: "Measures temperature of a reaction or bath.",
    why: "Modern lab thermometers are alcohol- or digital-based (mercury is phased out). Place the bulb in the liquid - not touching the glass - for an accurate reading.",
  },

  // --- Vessels ---
  {
    id: "test-tube",
    name: "Test tube",
    category: "vessel",
    use: "Holds small samples for observation, simple reactions, or heating.",
    why: "Cheap, disposable, and small-volume. Always heat at an angle, mouth pointing away from people, with constant gentle agitation to prevent bumping.",
  },
  {
    id: "watch-glass",
    name: "Watch glass",
    category: "vessel",
    use: "Covers a beaker or holds small solid samples for weighing.",
    why: "A shallow circular glass dish. Use it to cover a beaker (lets vapours escape but keeps dust out) or to hold a small mass of solid on a balance.",
  },
  {
    id: "evaporating-dish",
    name: "Evaporating dish",
    category: "heating",
    use: "Evaporates solvent from a solution to recover the solute.",
    why: "Wide, shallow porcelain dish - large surface area speeds evaporation. Heated gently to avoid spitting; commonly used to crystallise a salt from solution.",
  },
  {
    id: "crucible",
    name: "Crucible",
    category: "heating",
    use: "Heats solids to very high temperatures (red heat).",
    why: "Made from porcelain or ceramic to survive direct flame heating. Used with a lid for combustion analysis or when driving water of crystallisation from a hydrate.",
  },
  {
    id: "mortar-pestle",
    name: "Mortar and pestle",
    category: "vessel",
    use: "Grinds solids into a fine powder.",
    why: "A heavy ceramic bowl (mortar) and matching grinding tool (pestle). Increases surface area before reactions or dissolutions.",
  },

  // --- Heating ---
  {
    id: "bunsen-burner",
    name: "Bunsen burner",
    category: "heating",
    use: "Provides an open gas flame for heating glassware.",
    why: "Mixes gas with adjustable air at the base. Open the air hole for a hot blue (roaring) flame; close it for a luminous yellow (safety) flame that's more visible but cooler and sooty.",
  },
  {
    id: "hot-plate",
    name: "Hot plate",
    category: "heating",
    use: "Provides controlled, flame-free heating - safer for flammable liquids.",
    why: "Electric heating surface with a temperature dial. Preferred over a Bunsen burner for organic solvents because there's no open flame.",
  },
  {
    id: "tripod",
    name: "Tripod",
    category: "support",
    use: "Supports glassware over a Bunsen burner.",
    why: "Three-legged metal stand. Used together with a wire gauze to spread the flame evenly under a beaker or flask.",
  },
  {
    id: "wire-gauze",
    name: "Wire gauze",
    category: "support",
    use: "Spreads heat evenly under glassware on a tripod.",
    why: "A square mesh of wire (often with a ceramic centre). Sits on the tripod to protect the glass from direct flame and to even out the heat.",
  },

  // --- Support & handling ---
  {
    id: "retort-stand",
    name: "Retort stand and clamp",
    category: "support",
    use: "Holds glassware (burettes, condensers, flasks) in place.",
    why: "A heavy base + vertical rod. Bosses and clamps grip apparatus securely so your hands stay free and the set-up doesn't tip.",
  },
  {
    id: "test-tube-rack",
    name: "Test tube rack",
    category: "support",
    use: "Holds multiple test tubes upright for observation.",
    why: "Plastic, wooden, or wire rack with holes sized for standard test tubes. Keeps samples organised and prevents spills while you compare reactions.",
  },
  {
    id: "crucible-tongs",
    name: "Crucible tongs",
    category: "support",
    use: "Lifts hot crucibles, evaporating dishes, and beakers.",
    why: "Long metal tongs with curved jaws. Always use them - never bare hands or paper towels - to handle anything just heated.",
  },

  // --- Separation ---
  {
    id: "separating-funnel",
    name: "Separating funnel",
    category: "separation",
    use: "Separates two immiscible liquids by density (e.g., oil and water).",
    why: "Pear-shaped funnel with a stopcock. Shake to mix, vent the pressure, let the layers separate, then drain the lower (denser) layer through the stopcock.",
  },
  {
    id: "filter-funnel",
    name: "Filter funnel",
    category: "separation",
    use: "Holds filter paper to separate a solid from a liquid.",
    why: "A simple cone-shaped funnel. Fold a filter paper into a cone, place it inside, and pour through - the residue stays on the paper and the filtrate collects below.",
  },
  {
    id: "condenser",
    name: "Liebig condenser",
    category: "separation",
    use: "Cools and condenses vapour back to liquid in distillation.",
    why: "A tube within a tube: vapour passes through the inner tube while cold water flows in the outer jacket (counter-current). Used in simple distillation to collect the distillate.",
  },
  {
    id: "desiccator",
    name: "Desiccator",
    category: "vessel",
    use: "Stores samples in a dry atmosphere to prevent hygroscopic uptake.",
    why: "Sealed glass chamber with a drying agent (e.g., silica gel) at the bottom. Used to cool a heated crucible without it absorbing water from the air before weighing.",
  },
];

export const GLASSWARE_BY_CATEGORY: Record<GlasswareCategory, GlasswareItem[]> = glasswareItems.reduce(
  (acc, item) => {
    (acc[item.category] ||= []).push(item);
    return acc;
  },
  {} as Record<GlasswareCategory, GlasswareItem[]>,
);
